import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { all, any, equals, head, ifElse, map, pipe, prop, propEq } from 'ramda'
import { Button } from '@machobear/design-system'

import { colors } from '@/styles'
import { isGroup } from '@/utils/listings'
import useSessionComplete from '@/hooks/use-session-complete'
import { useMessageModalContext } from '@/features/ListingsMap/map-list/context'
import CdnImage from '@/components/cdn-image'
import PriceDisplay from '@/components/listing-card/pricing-display'
import { PillWrapper, VerifiedPill } from '@/components/pill'
import Skeleton from '@/components/skeleton'
import { Label } from '@/components/text'

import defaultBuildingImage from '../default-building.jpg'

const Outer = styled.div`
  position: relative;
  text-align: left;
  padding: 0;
  cursor: pointer;
  text-decoration: none;
  width: 100%;
  box-shadow: 0px 0px 0px 1px ${colors.grey};
  border-radius: 10px;

  &:hover,
  &:focus {
    > div:first-of-type {
      &:after {
        background: rgba(255, 255, 255, 0.1);
      }
      button {
        opacity: 1;
        transition: opacity 0.2s ease-in-out 0s;
      }
    }
  }
`

const Wrapper = styled.div`
  &:before {
    z-index: -5;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 105%;
    background: ${colors.white};
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0);
    transition: background-color 0.2s ease-in-out;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  width: 100%;
  position: relative;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  height: 216px;

  display: flex;
  align-items: flex-end;
  overflow: hidden;
  animation: fadeIn ease-in-out 0.2s;

  @keyframes fadeIn {
    0% {
      opacity: 0.2;
    }
    100% {
      opacity: 1;
    }
  }
`

const Bottom = styled.div`
  width: 100%;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  height: 206px;
  background: ${colors.white};
  border-top: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const Divider = styled.div`
  position: relative;
  height: 1px;
  border-top: 1px solid ${colors.grey88};
`

const Address = styled(Label)`
  color: ${colors.grey24};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 210px;
`

const BlueText = styled(Address)`
  color: ${colors.grey32};
  margin-top: 4px;
`

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const DetailText = styled(Label)`
  white-space: nowrap;
  color: ${colors.grey32};
`

const PrimaryInfoWrapper = styled.div``

const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  margin-bottom: 4px;
`

const Ellipsis = styled.div`
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const renderAddress = (building, hideStreetNumber) => {
  return hideStreetNumber ? building.street_name : building.full_street_name
}

const getCover = images => {
  if (!images?.length) return false
  const cover = images.find(({ tag }) => tag === 'cover_photo')
  return cover?.aws_s3_key ?? images[0].aws_s3_key
}

const BuildingCard = ({
  building,
  onMouseEnter,
  onMouseLeave,
  onClick,
  loading,
  index,
  trackImpression = {}
}) => {
  const { t } = useTranslation()
  const isLoggedIn = useSessionComplete()
  const { countDownStarted, isAutoSendingMessage } = useMessageModalContext()
  const buildingImage =
    getCover(building?.building_files) ||
    building?.listings[0]?.cover_photo_aws_s3_key
  const cover = buildingImage || defaultBuildingImage

  const unitCount = building?.listings?.length

  const hasMlsListings = any(propEq('public_viewing', false))(
    building?.listings
  )
  const hasBlockAppListings = any(propEq('allow_applications', false))(
    building?.listings
  )
  const hasVerifiedListing = any(propEq('verified_state_machine', 'approved'))(
    building?.listings
  )

  // Only display price/price range if all listings in building have the
  // same frequency, otherwise we show 'Various rates available'
  const priceFrequencies = map(prop('price_frequency'), building.listings)
  const frequency = head(priceFrequencies)
  const allListingsHaveSameFrequency = all(equals(frequency), priceFrequencies)
  const minListingPrice = Math.min(
    ...map(
      pipe(ifElse(isGroup, prop('gr_min_price'), prop('price'))),
      building.listings
    )
  )
  const maxListingPrice = Math.max(
    ...map(
      pipe(ifElse(isGroup, prop('gr_max_price'), prop('price'))),
      building.listings
    )
  )
  const price = { min: minListingPrice, max: maxListingPrice }

  const hideStreetNumber =
    building.building_type === 'house' &&
    any(propEq('hide_unit_number', true), building.listings)

  const getDataLayerEvent = () => {
    if (hasMlsListings) {
      return { event: 'mls-building-card-verified', listingCard: 'mlsbuilding' }
    } else if (hasBlockAppListings) {
      return {
        event: 'blockapp-building-card-verified',
        listingCard: 'blockapplicationbuilding'
      }
    } else if (hasVerifiedListing) {
      return {
        event: 'building-card-verified',
        listingCard: 'verifiedbuilding'
      }
    } else {
      return { event: 'building-card-regular', listingCard: 'regularbuilding' }
    }
  }

  const handleOnClick = () => {
    onClick()
    window.dataLayer.push(getDataLayerEvent())
  }

  const variousRatesText = t(
    'b.listing.view.building_card_various_rates.text',
    'Various rates available'
  )

  const Pills = () => (
    <PillWrapper>{hasVerifiedListing && <VerifiedPill />}</PillWrapper>
  )

  return (
    <Outer
      onClick={handleOnClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="group"
      aria-label={`Most relevant listing ${index + 1}`}
      data-impression-listingid={[building.listings.map(l => l.listing_id)]}
      data-impression-page={trackImpression.page}
      data-impression-section={trackImpression.section}
      data-impression-sponsored={trackImpression.sponsored}
      data-impression={trackImpression.enabled}
    >
      <>
        <Wrapper>
          <CdnImage
            src={cover}
            sizes="345px"
            layout="fill"
            objectFit="cover"
            placeholder="shimmer"
            alt="building image"
          />
        </Wrapper>
        <Pills />
        <Bottom>
          <PrimaryInfoWrapper>
            <PriceRow>
              <PriceDisplay
                price={price}
                frequency={frequency}
                showRange
                fallback={!allListingsHaveSameFrequency && variousRatesText}
                loading={loading}
              />
            </PriceRow>
            <DetailRow>
              <DetailText fontSize="16px">
                {t('b.listing.view.count_units_available.text', {
                  defaultValue: '{{ count }} units available',
                  count: unitCount
                })}
              </DetailText>
            </DetailRow>
          </PrimaryInfoWrapper>
          <Divider />
          {loading ? (
            <Ellipsis>
              <Skeleton width={120} />
              <Skeleton width={180} top={4} />
            </Ellipsis>
          ) : (
            <Ellipsis>
              <Address fontSize="14px" type="large">
                {renderAddress(building, hideStreetNumber)}
              </Address>
              <BlueText fontSize="12px">
                {`${building.city}, ${building.state}`}
                {!!building.building_name && ` · ${building.building_name}`}
              </BlueText>
            </Ellipsis>
          )}
          <Button
            disabled={countDownStarted || isAutoSendingMessage}
            id={
              isLoggedIn
                ? 'logged-view-avail-units-click'
                : 'listing-view-avail-units-click'
            }
          >
            {t(
              'b.listing.view.view_available_units.button',
              'View Available Units'
            )}
          </Button>
        </Bottom>
      </>
    </Outer>
  )
}
export default BuildingCard
